import { Layout } from 'antd';
import Axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useLocation, withRouter } from 'react-router-dom';
import { v4 } from 'uuid';
import { PdfContainer } from '../../../common/pdf/PdfContainer';
import { getClientInboxFile, getClientInboxFilePage, updateInboxFileTag } from '../../../lib/api/inbox-api';
import { getInboxOrResultFileLabels } from '../../../lib/labels';
import { reformInboxFileToPdfFile } from '../../../lib/util/reform';
import useGlobal from '../../../store/Store';
import { InboxFile, PdfFile, Pagination, InboxFilters } from '../../../types';
import { FeedbackField, ResultField } from '../../../types/result';
import InboxMenuSideBar from './InboxMenuSidebar';
import ResultSideBar from './ResultSideBar';
import { TopBar } from '../../../components/layout/TopBar';
import { DetailContainer } from '../../../components/layout/DetailContainer';
import { useGetClientAndInboxes, useGetFlows, useGetInboxFileList } from '../../../lib/hooks';
import {
  renderFilterString,
  setFiltersOnMount,
  initialFilterValues,
  createQueryStringFirstTimeForPageFetch,
} from '../Filters';
import { CustomSider } from '../../../components/layout/CustomSider';
import { Spinner } from '../../../components/loading/Spinner';
import { Section } from '../../../components/Section';

const { Content } = Layout;

const defaultPaging = {
  pageSize: 10,
  page: 0,
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ReviewResult = () => {
  let history = useHistory();
  const location = useLocation();
  const query = useQuery();

  const { fileId, doctype, clientName } = useParams() as {
    fileId: string;
    doctype: string;
    clientName: string;
  };

  const [inboxFile, setInboxFile] = useState<InboxFile | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [inboxFileStatus, setInboxfileStatus] = useState<string>('');
  const [pdfFile, setPdfFile] = useState<PdfFile | null>();
  const [globalState, globalActions] = useGlobal();

  const [paginationObject, setPaginationObject] = useState<Pagination | null>(null);

  const [queryString, setQueryString] = useState<string>('');
  const { flowOptions } = useGetFlows(clientName);

  const { inbox } = useGetClientAndInboxes(clientName, doctype);

  const { inboxFiles, filterItemsCount, filesLoading, refetchFiles } = useGetInboxFileList(
    clientName,
    paginationObject || null,
    doctype,
    queryString
  );

  const [filters, setFilters] = useState<InboxFilters>(initialFilterValues);

  //ONMOUNT: get the query parameters and set filters on these values
  useEffect(() => {
    setFiltersOnMount(query, setFilters);
    //we dont want query in dependency arry => infinite loop:
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getPage = async () => {
      const queryString = createQueryStringFirstTimeForPageFetch(query);
      const page = await getClientInboxFilePage(
        clientName,
        doctype,
        fileId,
        'size=' + defaultPaging.pageSize + queryString
      );
      console.log(page);
      setPaginationObject(prev => ({ pageSize: prev?.pageSize || defaultPaging.pageSize, page: page - 1 }));
    };

    if (!query.get('page')) {
      getPage();
    } else {
      setPaginationObject({
        pageSize: defaultPaging.pageSize,
        page: query.get('page') ? parseInt(query.get('page')!) : defaultPaging.page,
      });
    }
    //we dont want query in dependency arry => infinite loop:
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    globalActions.setLabelOptions(null);
    globalActions.setLabelEdit(null);
    globalActions.removeLabels();
    setInboxFile(null);
  }, [globalActions, setInboxFile]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const file = await getClientInboxFile(fileId);
    globalActions.setTags([file.tag]);
    if (inboxFiles.length) {
      if (file) {
        const resultsJson = await Axios.get(file.resultsJson);
        file.resultFields = resultsJson.data.fields.map((resultField: ResultField) => ({
          ...resultField,
          id: v4(),
        }));
        file.resultFields = file.resultFields.map((inboxFileResultField: ResultField) => {
          const hasCorrection: undefined | FeedbackField =
            (file.feedback?.fields.length &&
              file.feedback.fields.find(feedback => feedback.name === inboxFileResultField.name)) ||
            undefined;
          return { ...inboxFileResultField, hasCorrection: hasCorrection || null };
        });
        const labels = getInboxOrResultFileLabels(file);
        globalActions.setLabels(labels);
        globalActions.setProperties({ tag: file.tag, annotationState: '' });
        setInboxFile(file);
        setPdfFile(reformInboxFileToPdfFile(file));
        setLoading(false);
      } else {
        setInboxFile(null);
        setLoading(false);
      }
    }
  }, [fileId, globalActions, inboxFiles]);

  //FILTER & SORTING

  const checkPagesAfterFilter = useCallback(() => {
    if (paginationObject) {
      const pageMinCapacity = paginationObject.page * paginationObject.pageSize;
      if (filterItemsCount > 0 && filterItemsCount < pageMinCapacity) {
        setPaginationObject(prev => ({ ...(prev || defaultPaging), page: 0 }));
      }
    }
  }, [filterItemsCount, paginationObject]);

  //set filterquerystring whenever filter updates
  useEffect(() => {
    setQueryString(renderFilterString(filters));
    checkPagesAfterFilter();
  }, [filters, checkPagesAfterFilter]);

  useEffect(() => {
    if (paginationObject) {
      history.replace({
        pathname: location.pathname,
        search: 'page=' + paginationObject.page + '&size=' + paginationObject.pageSize + queryString,
      });
    }
  }, [history, location.pathname, paginationObject, queryString]);

  //OTHER FUNCTIONS

  const getStatusFromFile = async (fileId: string) => {
    const file = await getClientInboxFile(fileId);
    setInboxfileStatus(file.status);
  };

  useEffect(() => {
    reset();
    if (fileId) {
      fetchData();
    }
    getStatusFromFile(fileId);
  }, [fileId, fetchData, reset]);

  useEffect(() => {
    if (inboxFiles.length === 0) {
      setInboxFile(null);
      setPdfFile(null);
    }
  }, [inboxFiles]);

  const goBack = () => {
    history.push(
      `/inbox/${clientName}/${doctype}/overview?${
        paginationObject?.page ? `page=${paginationObject.page}` : ''
      }${queryString}`
    );
  };

  const switchToFile = useCallback(
    (fileId: any) => {
      return `/inbox/${clientName}/${doctype}/file/${fileId}?${
        paginationObject?.page ? 'page=' + paginationObject.page : ''
      }${queryString}`;
    },
    [clientName, doctype, queryString, paginationObject]
  );

  const { labels } = globalState;
  const filteredLabels = useMemo(() => labels.filter(label => label.exists || !label.reliable), [labels]);

  const onPropertiesChange = async (properties: { tag: string; annotationState: string }) => {
    await updateInboxFileTag(fileId, properties.tag);
    refetchFiles(false);
  };

  return (
    <>
      <TopBar
        title="Annotate Documents"
        closeFunction={(event: any) => {
          event.preventDefault();
          event.stopPropagation();
          goBack();
        }}
      />
      <DetailContainer>
        {paginationObject && (
          <InboxMenuSideBar
            selectedId={fileId}
            inbox={inbox}
            inboxList={inboxFiles}
            filterItemsCount={filterItemsCount}
            loading={filesLoading}
            paginationObject={paginationObject}
            setPaginationObject={setPaginationObject}
            readOnly={globalState.readOnly}
            onClickUrl={(fileId: string) => {
              return switchToFile(fileId);
            }}
            filters={filters}
            setFilters={setFilters}
            flowOptions={flowOptions}
          />
        )}
        <Layout>
          <Content>
            <div className={'pdf-layout'} style={{ width: '100%', height: '100%' }}>
              {loading ? (
                <Spinner />
              ) : (
                <PdfContainer
                  file={pdfFile}
                  labels={filteredLabels.filter(label => !label.hidden)}
                  showNonExisting={true}
                  newLabelAllowed={true}
                  onAnnotationChange={() => {}}
                  withCorrections={true}
                />
              )}
            </div>
          </Content>
        </Layout>
        <CustomSider width={400} border="left" style={{ paddingTop: '1.25rem' }}>
          {loading ? (
            <Spinner />
          ) : inboxFile && !loading ? (
            <ResultSideBar
              inboxFile={inboxFile}
              inboxFileStatus={inboxFileStatus}
              getStatusFromFile={getStatusFromFile}
              onPropertiesChange={onPropertiesChange}
            />
          ) : (
            <Section>
              <span>No file selected</span>
            </Section>
          )}
        </CustomSider>
      </DetailContainer>
    </>
  );
};

export default withRouter(ReviewResult);
